import { format } from 'date-fns';
import { memo, useMemo, type MouseEvent as ReactMouseEvent } from 'react';
import { Center } from '~/commonComponents/Center';
import { Column } from '~/commonComponents/Column';
import { Row } from '~/commonComponents/Row';
import { Text } from '~/commonComponents/Text';
import { CloseIcon } from '~/images/icons';
import PlusIcon from '~/images/icons/PlusIcon';
import { Colors } from '~/themes/colors';
import { Value } from '../Field';

interface ICustomDateValueProps {
  format?: string;
  isDisabled?: boolean;
  values?: Date[];
  onAddDatesClick?(): void;
  onRemoveDate?(value: Date, index: number): void;
}

const Component = ({
  format: dateLayout = 'EEE, MMM dd, yyyy',
  isDisabled = false,
  values = [],
  onAddDatesClick,
  onRemoveDate,
}: ICustomDateValueProps) => {
  const hasValues = values.length > 0;

  const fmtDates = useMemo(() => values.map((it) => format(it, dateLayout)), [dateLayout, values]);

  const handleAddDatesClick = (e: ReactMouseEvent) => {
    e.stopPropagation();
    onAddDatesClick?.();
  };

  const handleRemoveDateClick = (index: number) => (e: ReactMouseEvent) => {
    e.stopPropagation();
    onRemoveDate?.(values[index], index);
  };

  if (!hasValues) {
    return <Value style={{ color: Colors.neutral_300 }} text="Dates" />;
  }

  return (
    <Column alignItems="flex-end" gap={15}>
      {fmtDates.map((it, i) => (
        <Row data-testid={`input-job.date.item-${it}`} key={it}>
          <Text data-testid={`input-job.date.item-${it}.text`} textTransform="capitalize">
            {it}
          </Text>
          {!isDisabled && (
            <Center
              data-testid={`input-job.date.item-${it}.close-icon`}
              cursor="pointer"
              marginLeft={30}
              marginRight={-30}
              role="button"
              onClick={handleRemoveDateClick(i)}
            >
              <CloseIcon color={Colors.secondary_500} />
            </Center>
          )}
        </Row>
      ))}

      {!isDisabled && (
        <Row
          alignItems="center"
          cursor="pointer"
          data-testid={`input-job.date.add-more-dates`}
          gap={8}
          role="button"
          onClick={handleAddDatesClick}
        >
          <PlusIcon color={Colors.secondary_500} />
          <Text bold color={Colors.secondary_500} fontSize={18}>
            Add Dates
          </Text>
        </Row>
      )}
    </Column>
  );
};

export const CustomDateValue = memo(Component);
